import { defineStore } from "pinia";
import { jwtDecode } from "jwt-decode";
import { ApiEndpoint, isProd, isFirebase, devUserId } from "@/utils/config";
import { getAuth, onAuthStateChanged, signOut } from 'firebase/auth';

export const useAuthStore = defineStore('auth', {
  state: () => ({
    isHydrated: false,
    jwt: null,
    customerId: null,
    customerName: null,
    role: null,
    userName: null,
    userId: null,
  }),
  persist: {
    paths: ['customerId', 'customerName', 'role', 'userName', 'userId'],
  },
  getters: {
    isAuthenticated: (state) => {
      return !!state.jwt;
    },
    isAdmin: (state) => {
      return ['VIASAT_ADMIN', 'ADMIN'].includes(state.role);
    },
    isViasat: (state) => {
      return ['ADMIN', 'VIASAT_ADMIN', 'VIASAT'].includes(state.role);
    },
    isCustomer: (state) => {
      return state.customerId && state.customerId !== 'viasat';
    },
    isCustomerAdmin: (state) => {
      return ['CUSTOMER_ADMIN'].includes(state.role);
    },
    isProvisioningAllowed: (state) => {
      return this.isViasat(state) || this.isCustomerAdmin(state);
    },
    isDecodedSyncAllowed: (state) => {
      return this.isViasat(state) || this.isCustomerAdmin(state);
    },
  },
  actions: {
    login(authInfo) {
      try {
        const { token, expiresIn, tokenType } = authInfo;
        const userData = jwtDecode(token);
        this.jwt = token;
        this.customerId = userData.customerId;
        this.customerName = userData.customerName || null;
        this.role = userData.role;
        this.userName = userData.name;
        this.userId = userData.id;
        console.debug(`Logged in ${this.userName}`);
      } catch (err) {
        console.error(`Login failed (${err})`);
        this.logout();
        throw err;
      }
    },
    async logout(reason) {
      if (typeof reason === 'string' && reason.length > 0) {
        console.debug(`Logging out ${this.userName} (${reason})`);
      }
      if (isFirebase) {
        try {
          const fbAuth = getAuth();
          await signOut(fbAuth);
        } catch (err) {
          console.error("Failed tearing down Firebase client session", err);
        }
      }
      this.$reset();
      localStorage.removeItem('auth');
    },
    async checkSession() {
      const devMode = (!isProd && devUserId);
      const endpoint = false ? ApiEndpoint.TOKEN : ApiEndpoint.AUTHME;
      const method = false ? 'POST' : 'GET';
      const body = false ? {} : undefined;
      // For IAP/IAM the infrastructure will intercept and apply its header(s)
      const headers = new Headers({ 'Content-Type': 'application/json' });
      if (devMode) {
        headers.append('x-dev-user', devUserId);
      } else if (isFirebase) {
        // Wait for Firebase SDK to initialize
        const firebaseUser = await new Promise((resolve) => {
          const unsubscribe = onAuthStateChanged(getAuth(), (user) => {
            unsubscribe();
            resolve(user);
          });
        });
        if (!firebaseUser) {
          this.logout();
          this.isHydrated = true; // App completed check, but user is anonymous
          throw new Error('No Firebase user logged in');
        }
        const token = await firebaseUser.getIdToken();
        headers.append('authorization', `Bearer ${token}`);
      }
      try {
        const res = await fetch(endpoint, {
          method: method,
          headers: headers,
          body: body ? JSON.stringify(body) : undefined,
        });
        if (!res.ok) {
          this.logout();
          const errInfo = await res.json();
          throw new Error(errInfo.message || errInfo.error);
        }
        const authInfo = await res.json();
        return this.login(authInfo);
      } finally {
        this.isHydrated = true;
      }
    },
  }
});
